'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useEffect } from 'react';

import { useT } from '@/app/i18n/client';
import type { IProductOptions, ProductItemType } from '@/entities/product/types';
import { CurrencyProvider } from '@/features/product/contexts/CurrencyContext';
import { productOptions } from '@/features/product/options/product';
import ProductHtml from '@/features/product/ui/Html';
import { useProductStore } from '@/stores/product';

import ProductAccommodationSection from './Accommodation';
import ActivitySection from './Activity';
import ProductCartSection from './Cart';
import SlotSection from './Slot';

export default function ProductMainSection({ idx, lng }: IProductOptions) {
  const { t } = useT('product');

  const { data: product } = useSuspenseQuery(productOptions({ idx, lng }));
  const { itemList = [] } = product;

  const setCurrentProductIdx = useProductStore(state => state.setCurrentProductIdx);
  // idx가 변경될 때마다 ProductStore 체크 및 초기화
  useEffect((): void => {
    setCurrentProductIdx(idx);
  }, [idx, setCurrentProductIdx]);

  return (
    <main className='mx-auto w-full max-w-7xl p-5 max-lg:pb-12'>
      {product.detailImage?.url && (
        <Image
          src={product.detailImage?.url}
          alt={product.title}
          className='mb-5 aspect-[3.204/1] w-full rounded-xl object-cover object-center max-sm:aspect-[1.6/1]'
          width={1160}
          height={362}
          quality={100}
        />
      )}

      <CurrencyProvider value={product.currency}>
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          <article className='lg:col-span-2'>
            <h1 className='mb-8 break-words text-4xl font-bold text-[#2E2E2E] max-sm:text-2xl'>
              {product.title}
            </h1>

            {/* Product Items */}
            <section aria-labelledby='product-items-heading' className='mb-8 space-y-5'>
              <h2 id='product-items-heading' className='sr-only'>
                Product Items
              </h2>
              {itemList.map(item => (
                <ProductItem key={item.idx} item={item} />
              ))}
            </section>

            {/* Product Description */}
            <section aria-labelledby='product-description-heading'>
              <h2 id='product-description-heading' className='sr-only'>
                Product Description
              </h2>
              <ProductHtml html={product.description} />
            </section>

            {/* Product Cancellation Policy */}
            <section className='mt-8'>
              <h2
                id='product-cancellation-policy-heading'
                className='mb-2 text-2xl font-bold text-[#2E2E2E] max-sm:text-xl'
              >
                {t('cancellationPolicy')}
              </h2>
              <ProductHtml html={product.cancellationPolicy} />
            </section>

            {product?.additionalInfo && (
              <section className='mt-8'>
                <h2
                  id='product-cancellation-policy-heading'
                  className='mb-2 text-2xl font-bold text-[#2E2E2E] max-sm:text-xl'
                >
                  {t('additionalInfo')}
                </h2>
                <ProductHtml html={product.additionalInfo} />
              </section>
            )}
          </article>

          {/* Cart */}
          <ProductCartSection cancellationPolicy={product.cancellationPolicy} />
        </div>
      </CurrencyProvider>
    </main>
  );
}

const ProductItem = ({ item }: { item: ProductItemType }) => {
  switch (item.type) {
    case 'ACCOMMODATION':
      return <ProductAccommodationSection item={item} />;
    case 'ACTIVITY':
      return <ActivitySection item={item} />;
    default:
      return <SlotSection item={item} />;
  }
};
